import { useTranslate } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCad, formatPercent } from "../domain/format";
import { useReportsClient } from "../reports/ReportsClientContext";
import { useReport } from "../reports/useReport";
import { todayRange } from "./dashboardDates";
import { FunnelMini } from "./FunnelMini";
import { IntegrationHealthStrip } from "./IntegrationHealthStrip";
import { KpiTile } from "./KpiTile";
import { Leaderboard } from "./Leaderboard";
import { LiveSessionsCard } from "./LiveSessionsCard";
import { useNow } from "./useNow";

/** Admin "Équipe" view (spec 09 §4): team counters, pipeline, leaderboard, funnel. */
export const TeamDashboard = () => {
  const translate = useTranslate();
  const client = useReportsClient();
  const now = useNow(60_000);
  const { from, to } = todayRange(now);
  const q = useReport(["activity", "team", from, to], () =>
    client.activity(from, to, { scope: "team" }),
  );
  if (q.isPending) return <Skeleton className="h-40 w-full rounded-xl" />;
  if (!q.data)
    return (
      <p role="alert" className="text-sm text-destructive">
        {translate("gavion.reports.error")}
      </p>
    );
  const { totals, pipeline, by_rep } = q.data;
  return (
    <div className="space-y-6">
      <IntegrationHealthStrip />
      <LiveSessionsCard />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiTile
          label={translate("gavion.dashboard.kpi.team_dials")}
          value={totals.dials}
        />
        <KpiTile
          label={translate("gavion.dashboard.kpi.team_contacts")}
          value={totals.contacts}
        />
        <KpiTile
          label={translate("gavion.dashboard.kpi.contact_rate")}
          value={formatPercent(totals.contact_rate)}
        />
        <KpiTile
          label={translate("gavion.dashboard.kpi.team_demos")}
          value={totals.demos}
        />
        <KpiTile
          label={translate("gavion.dashboard.kpi.pipeline_value")}
          value={formatCad(pipeline.open_amount)}
          hint={translate("gavion.dashboard.kpi.weighted", {
            amount: formatCad(pipeline.weighted_amount),
          })}
        />
        <KpiTile
          label={translate("gavion.dashboard.kpi.won_month")}
          value={formatCad(pipeline.won_this_month)}
        />
      </div>
      <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
        <section className="min-w-0 overflow-x-auto rounded-xl border bg-card p-4">
          <Leaderboard rows={by_rep} />
        </section>
        <FunnelMini now={now} />
      </div>
    </div>
  );
};
